/**
 * Admin panelindeki TÜM yol string'leri — TEK kaynak.
 *
 * `<AppLink to="/control-center">` yerine `<AppLink to={paths.controlCenter}>`;
 * böylece yeniden adlandırma tek satır kalır. (2026-07 "Bildirimler → Kontrol
 * Merkezi" sırasında `/notifications` üç ayrı dosyada elle yazılmıştı.)
 *
 * Her girdi `toRoutePath()`'ten geçer → dönen değer `RoutePath` markasını taşır.
 * `lib/route-primitives.tsx`'teki sarmalayıcılar YALNIZ `RoutePath` kabul ettiği
 * için, bu dosyanın dışında üretilmiş hiçbir string navigasyon hedefi olamaz —
 * kaç ara değişkenden geçerse geçsin tsc reddeder.
 */
import { toRoutePath, type RoutePath } from "@wedding/shared";

/** "Satış & Site" sekmeleri — fazlarla büyür (docs/plans/2026-10-13-satis-site-yonetimi.md). */
export type SiteTab = "pricing" | "leads" | "proof" | "media" | "retention" | "demo" | "sales" | "legal" | "ledger";

export const paths = {
  root: toRoutePath("/"),
  /**
   * GÖRELİ hedef: "içinde bulunduğun rota eşleşmesinin index'i".
   * Düğün-içi `<Route path="*">` catch-all'ı bilinmeyen alt-yolu düğün
   * dashboard'una geri düşürmek için kullanır — slug'ı bilmeye gerek kalmadan.
   * Mutlak yol OLMADIĞI için `/` ile başlamaz; yine de bir navigasyon hedefi
   * olduğundan buradan, `toRoutePath()` üzerinden geçer.
   */
  self: toRoutePath("."),
  users: toRoutePath("/users"),
  userDetail: (id: string): RoutePath => toRoutePath(`/users/${id}`),
  /** Kullanıcı listesi, plan filtresi önceden seçili. */
  usersFilteredByPlan: (planSlug: string): RoutePath => toRoutePath(`/users?plan=${planSlug}`),
  plans: toRoutePath("/plans"),
  audit: toRoutePath("/audit"),
  /** Audit log, tek bir kimliğe (organizatör e-postası) filtreli. */
  auditForIdentity: (email: string): RoutePath =>
    toRoutePath(`/audit?identity=${encodeURIComponent(email)}`),
  support: toRoutePath("/support"),
  supportDetail: (id: string): RoutePath => toRoutePath(`/support/${id}`),
  // UI adı "Hata Takibi" (nav.maintenance) — yol da buna hizalandı.
  // ⚠️ Worker API yolu `/api/admin/maintenance/*` BİLEREK aynı kaldı: Pages ve
  // Worker bağımsız deploy oluyor, API yolunu değiştirmek deploy aralığında 404
  // üretir. Buradaki yollar YALNIZ tarayıcı rotalarıdır.
  maintenance: toRoutePath("/error-tracking"),
  maintenanceGroup: (fingerprint: string): RoutePath =>
    toRoutePath(`/error-tracking/${fingerprint}`),
  controlCenter: toRoutePath("/control-center"),
  /** Çapraz-wedding SMS Merkezi (047 + 048) — per-wedding SMS sayfasından AYRI. */
  smsCenter: toRoutePath("/sms-center"),
  /**
   * SMS Merkezi'nin sekmesi query param'da: sekmeler `<button>` değil LİNK, o
   * yüzden hedefleri de markalı `RoutePath` olmak zorunda (elle yazılan rota
   * derlenmez). `pending` varsayılan → temiz URL, param eklenmez.
   */
  smsCenterTab: (tab: "pending" | "all"): RoutePath =>
    toRoutePath(tab === "pending" ? "/sms-center" : `/sms-center?tab=${tab}`),
  themes: toRoutePath("/themes"),
  /** "Satış & Site" — landing fiyatları vb. Sekme query param'da (`pricing` varsayılan → temiz URL). */
  site: toRoutePath("/site"),
  /** KVKK başvuruları — telefonla bul → sil/anonimleştir (düğünler arası). */
  privacy: toRoutePath("/privacy"),
  siteTab: (tab: SiteTab): RoutePath => toRoutePath(tab === "pricing" ? "/site" : `/site?tab=${tab}`),
  wedding: {
    dashboard: (slug: string): RoutePath => toRoutePath(`/${slug}`),
    guests: (slug: string): RoutePath => toRoutePath(`/${slug}/guests`),
    moderation: (slug: string): RoutePath => toRoutePath(`/${slug}/moderation`),
    gallery: (slug: string): RoutePath => toRoutePath(`/${slug}/gallery`),
    sms: (slug: string): RoutePath => toRoutePath(`/${slug}/sms`),
    analytics: (slug: string): RoutePath => toRoutePath(`/${slug}/analytics`),
    print: (slug: string): RoutePath => toRoutePath(`/${slug}/print`),
    sahne: (slug: string): RoutePath => toRoutePath(`/${slug}/slideshow`),
    organizers: (slug: string): RoutePath => toRoutePath(`/${slug}/organizers`),
    themes: (slug: string): RoutePath => toRoutePath(`/${slug}/themes`),
    settings: (slug: string): RoutePath => toRoutePath(`/${slug}/settings`),
  },
} as const;
